import { Component, Input, OnInit, OnDestroy, Output, EventEmitter, ViewEncapsulation, OnChanges } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { calendarColors } from 'app/modules/calendar/sidebar/calendar-colors';
import orderBy from 'lodash-es/orderBy';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

@Component({
  selector: 'settings-sortable-dropdown-editor',
  templateUrl: './sortable-dropdown-editor.component.html',
  styleUrls: ['./sortable-dropdown-editor.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SortableDropdownEditorComponent implements OnInit, OnDestroy, OnChanges {
  @Input() options: any[];
  @Input() title: string;
  @Input() disableAddNew: boolean = false;
  @Input() showColor: boolean = false;
  @Input() relatedToOptionsMap: Map<string, string> = new Map<string, string>();
  @Output() onUpdate: EventEmitter<any> = new EventEmitter();
  @Output() onAdd: EventEmitter<any> = new EventEmitter();
  @Output() rankChanged = new EventEmitter();

  numOptions: number[] = [];

  dropdownForm: UntypedFormGroup;
  calendarColors: any;

  /**
   * Constructor
   *
   * @param {FormBuilder} _formBuilder
   */
  constructor(private _formBuilder: UntypedFormBuilder) {}

  ngOnInit() {
    this.calendarColors = calendarColors;

    this.renderOptions();
  }

  renderOptions() {
    this.dropdownForm = this._formBuilder.group({
      options: this._formBuilder.array([]),
      newOption: this._formBuilder.group({
        name: ['', Validators.required],
      }),
    });
    this.options = orderBy(this.options, 'rank', 'asc');
    this.options.forEach((option) => {
      const formGroup = this._formBuilder.group({
        id: [option.id],
        name: [option.name, Validators.required],
        hidden: [option.hidden],
        rank: [option.rank],
        relatedTo: [option.related_to_type],
        color: [option.color || 'gray'],
      });

      // Add the label form group to the labels form array
      (this.dropdownForm.get('options') as UntypedFormArray).push(formGroup);
    });

    this.numOptions = [];
    for (let i = 0; i < this.options.length; i++) {
      this.numOptions.push(i);
    }

    this.dropdownForm
      .get('options')
      .valueChanges.pipe(debounceTime(500))
      .subscribe(() => {
        this.updateOptions();
      });
  }

  ngOnChanges() {
    this.renderOptions();
  }

  /**
   * Update labels
   */
  updateOptions(): void {
    // Iterate through the labels form array controls
    (this.dropdownForm.get('options') as UntypedFormArray).controls.forEach((formGroup) => {
      // If the label has been edited...
      if (formGroup.dirty) {
        // Update the label on the server
        let option = {
          id: formGroup.value.id,
          name: formGroup.value.name,
          hidden: formGroup.value.hidden,
          rank: formGroup.value.rank,
        };
        this.onUpdate.emit(option);
      }
    });

    // Reset the labels form array
    this.dropdownForm.get('options').markAsPristine();
    this.dropdownForm.get('options').markAsUntouched();
  }

  ngOnDestroy() {}

  get showRelatedTo() {
    return this.relatedToOptionsMap.size > 0;
  }

  getRelatedToLabel(name: string): string {
    return name?.startsWith('co_') ? this.relatedToOptionsMap.get(name) : name;
  }

  showHideOption(formGroup): void {
    let option = formGroup.value;
    option.hidden = !option.hidden;
    this.onUpdate.emit(option);
    // Reset the labels form array
    formGroup.get('hidden').setValue(option.hidden);
  }

  /**
   * Add a Pipeline
   */
  addOption(): void {
    const val = this.dropdownForm.get('newOption').value;
    this.onAdd.emit(val);

    /*(this.dropdownForm.get('options') as FormArray).push(this._formBuilder.group({
           id   : [val.id],
           name: [val.name, Validators.required],
       }));*/
    // Reset the labels form array
    this.dropdownForm.get('newOption').markAsPristine();
    this.dropdownForm.get('newOption').markAsUntouched();
    this.dropdownForm.get('newOption.name').reset();
    this.dropdownForm.get('newOption.name').clearValidators();
    this.dropdownForm.get('newOption.name').updateValueAndValidity();
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.options, event.previousIndex, event.currentIndex);
    this.rankChanged.emit(this.options);
    this.rerank(this.options);
  }

  rerank(options) {
    options.forEach((e, i) => (e.rank = i));
    this.renderOptions();
  }
}
